import type { ContentRepository } from '@site/shared/ports'
import type { Locale } from '@/lib/i18n/messages'

/**
 * One descriptor per piece of CMS content: a cache key plus how to load it
 * from a repository. The SAME descriptor feeds both the React hook (browser)
 * and the route loader's server-side prefetch, so the key a loader prefetches
 * is by construction the key the component reads — no drift between the two.
 */
export type ContentQuery<T> = {
  key: readonly unknown[]
  load: (repo: ContentRepository) => Promise<T>
}

function query<T>(key: readonly unknown[], load: (repo: ContentRepository) => Promise<T>): ContentQuery<T> {
  return { key, load }
}

/** Full TanStack cache key: content is always locale-scoped. */
export function contentQueryKey(locale: Locale, q: ContentQuery<unknown>): readonly unknown[] {
  return [locale, ...q.key]
}

export const contentQueries = {
  page: (slug: string) => query(['page', slug], (r) => r.getPage(slug)),
  header: () => query(['header'], (r) => r.getHeader()),
  footer: () => query(['footer'], (r) => r.getFooter()),
  siteSettings: () => query(['siteSettings'], (r) => r.getSiteSettings()),
  materials: (category?: string) => query(['materials', category ?? 'all'], (r) => r.listMaterials({ category })),
  featuredMaterials: () => query(['materials', 'featured'], (r) => r.listMaterials({ featured: true })),
  material: (slug: string) => query(['material', slug], (r) => r.getMaterial(slug)),
  categories: () => query(['categories'], (r) => r.listCategories()),
  upcomingEvents: () => query(['events', 'upcoming'], (r) => r.listUpcomingEvents()),
  latestNews: () => query(['news', 'latest'], (r) => r.listNews(3)),
}
